<template>
  <div class="relative flex flex-col">
    <div v-if="label">
      <label
        :for="label"
        class="text-text-primary text-sm font-medium dark:text-text-primary-dark"
        :class="[labelClass, allowLabelWrap ? '' : 'label-wrapper']"
      >
        {{ label }}
        <span v-if="props.showRequiredLabel ?? required" class="ml-1">
          (Obligatorio)<span class="text-text-error">*</span>
        </span>
      </label>
    </div>
    <IconField :iconPosition="iconPosition" class="relative w-full">
      <!-- Icono a la izquierda -->
      <!-- Procesamiento del icono -->
      <InputIcon
        v-if="icon && iconPosition === 'left'"
        class="left-icon absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 flex items-center justify-center"
      >
        <font-awesome-icon
          :icon="icon"
          class="text-icon-left dark:text-text-primary-dark"
        />
      </InputIcon>

      <InputText
        v-model="model"
        :name="label"
        :type="inputType"
        :placeholder="placeholder"
        :disabled="disabled"
        :maxlength="textMaxLength"
        :minlength="textMinLength"
        @blur="handleEmitBlur($event)"
        class="w-full cursor-pointer dark:bg-surface-primary! dark:placeholder-text-primary-dark dark:border-dark-border dark:text-text-primary-dark!"
        :class="{
          'border-helper-text!': hasError,
          'pl-10!': icon && iconPosition === 'left',
          'pr-10!': eye || clearable || (icon && iconPosition === 'right'),
          [inputClass]: true,
        }"
        :aria-invalid="hasError"
        :aria-describedby="errorId"
      />

      <button
        v-if="icon && iconPosition === 'right'"
        type="button"
        class="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center"
        @click="$emit('icon-click')"
      >
        <font-awesome-icon
          :icon="icon"
          class="text-icon-right dark:text-text-primary-dark"
        />
      </button>

      <button
        v-if="eye"
        type="button"
        @click="togglePassword"
        class="absolute top-1/2 -translate-y-1/2 flex items-center justify-center"
        :class="icon && iconPosition === 'right' ? 'right-12' : 'right-4'"
      >
        <font-awesome-icon
          :icon="
            isPasswordVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'
          "
          class="text-icon-right dark:text-text-primary-dark"
        />
      </button>

      <button
        v-if="clearable"
        type="button"
        @click="clearInput"
        class="absolute top-1/2 -translate-y-1/2 flex items-center justify-center"
        :class="
          (icon && iconPosition === 'right') || eye ? 'right-12' : 'right-4'
        "
      >
        <font-awesome-icon
          icon="fa-solid fa-circle-xmark"
          class="text-icon-right dark:text-text-primary-dark"
        />
      </button>
    </IconField>
    <p
      v-if="helper"
      class="text-text-secondary text-[12px] mt-1.5 font-500 leading-4 tracking-[-0.2px] dark:text-text-primary-dark!"
    >
      <font-awesome-icon
        icon="fa-solid fa-circle-info"
        class="text-[11px] text-accent-sky-60 font-900 align-center leading-3"
        :class="{ 'text-text-secondary': disabled }"
      />
      {{ helper }}
    </p>

    <!-- Contenedor fijo para error/validación -->
    <div v-if="!props.noErrorContainer" class="h-4 mt-1 relative w-full">
      <transition name="fade">
        <p
          v-if="displayError"
          class="absolute left-0 top-0 text-helper-text text-[10px] leading-3 flex items-center gap-1 w-full"
          :id="errorId"
        >
          <font-awesome-icon
            icon="fa-solid fa-circle-xmark"
            class="text-helper-text shrink-0"
          />
          <span class="truncate flex-1" :title="displayError">{{
            displayError
          }}</span>
        </p>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

// ============================================================================
// Props
// ============================================================================
const props = defineProps({
  // Model
  modelValue: {
    type: [String, Number],
    default: "",
  },

  // Basic input props
  label: String,
  type: {
    type: String,
    default: "text",
  },
  placeholder: {
    type: String,
    default: "",
  },
  disabled: Boolean,

  // Icon props
  icon: {
    type: String,
    default: "",
  },
  iconPosition: {
    type: String,
    default: "left",
    validator: (value: string) => ["left", "right"].includes(value),
  },

  // Style props
  inputClass: {
    type: String,
    default: "",
  },
  labelClass: {
    type: String,
    default: "",
  },
  allowLabelWrap: {
    type: Boolean,
    default: false,
  },

  // Validation props
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
  minLength: {
    type: Number,
    default: 0,
  },
  maxLength: {
    type: Number,
    default: 0,
  },
  min: {
    type: [Number, String],
    default: null,
  },
  max: {
    type: [Number, String],
    default: null,
  },
  limitNumber: {
    type: Number,
    default: 0,
  },
  exactLengths: {
    type: Array as () => number[],
    default: () => [],
  },
  validationMode: {
    type: String,
    default: "onInput",
    validator: (value: string) =>
      ["onInput", "onBlur", "manual"].includes(value),
  },

  // Feature props
  helper: String,
  eye: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  checkInput: {
    type: Boolean,
    default: false,
  },
  showRequiredLabel: {
    type: Boolean,
    default: undefined,
  },
  noErrorContainer: {
    type: Boolean,
    default: false,
  },
});

// ============================================================================
// Emits
// ============================================================================
const emit = defineEmits([
  "update:modelValue",
  "blur-xs",
  "blur",
  "icon-click",
]);

// ============================================================================
// State
// ============================================================================
const model = ref(props.modelValue?.toString?.() ?? "");
const isPasswordVisible = ref(false);
const touched = ref(false);
const validationError = ref("");

// ============================================================================
// Computed Properties
// ============================================================================
const inputType = computed(() =>
  isPasswordVisible.value ? "text" : props.type,
);

const textMaxLength = computed(() =>
  props.type === "text"
    ? props.maxLength || (props.max != null ? Number(props.max) : undefined)
    : undefined,
);

const textMinLength = computed(() =>
  props.type === "text"
    ? props.minLength || (props.min != null ? Number(props.min) : undefined)
    : undefined,
);

const hasError = computed(() => Boolean(props.error || validationError.value));

const displayError = computed(() => {
  // Prioridad: error externo > error de validación interna
  if (props.error) return props.error;
  if (validationError.value) return validationError.value;
  return "";
});

const errorId = computed(() =>
  props.label ? `${props.label}-error` : undefined,
);

// ============================================================================
// Validation Logic
// ============================================================================
function validateRequired(value: string): string {
  if (props.required && touched.value && value.length === 0) {
    return "Este campo es obligatorio";
  }
  return "";
}

function validateNumber(value: string): string {
  if (props.type !== "number") return "";

  const length = value.length;
  if (props.limitNumber > 0 && length > 0 && length !== props.limitNumber) {
    return `Debe tener exactamente ${props.limitNumber} números`;
  }
  return "";
}

function validateExactLength(value: string): string {
  if (props.exactLengths.length === 0) return "";

  const length = value.length;
  if (length > 0 && !props.exactLengths.includes(length)) {
    return `Debe tener exactamente ${props.exactLengths.join(" o ")} caracteres`;
  }
  return "";
}

function validateMinMaxLength(value: string): string {
  const length = value.length;
  const min = props.minLength > 0 ? props.minLength : 0;
  const max = props.maxLength > 0 ? props.maxLength : 0;

  if (min > 0 && max > 0) {
    if (length > 0 && (length < min || length > max)) {
      if (min === max) {
        return `Debe tener exactamente ${min} caracteres`;
      }

      return `Debe tener entre ${min} y ${max} caracteres`;
    }
  } else if (min > 0) {
    if (length > 0 && length < min) {
      return `Debe tener al menos ${min} caracteres`;
    }
  } else if (max > 0) {
    if (length > max) {
      return `Debe tener máximo ${max} caracteres`;
    }
  }

  return "";
}

function getErrorForValue(value: string): string {
  // Validar en orden de prioridad
  const requiredError = validateRequired(value);
  if (requiredError) return requiredError;

  const numberError = validateNumber(value);
  if (numberError) return numberError;

  const exactLengthError = validateExactLength(value);
  if (exactLengthError) return exactLengthError;

  const minMaxError = validateMinMaxLength(value);
  if (minMaxError) return minMaxError;

  return "";
}

function runValidation(): void {
  validationError.value = getErrorForValue(model.value);
}

// ============================================================================
// Methods
// ============================================================================
function togglePassword(): void {
  isPasswordVisible.value = !isPasswordVisible.value;
}

function clearInput(): void {
  model.value = "";
  validationError.value = "";
  touched.value = false;
  emit("update:modelValue", "");
}

function handleEmitBlur(event: Event): void {
  touched.value = true;

  if (props.validationMode !== "manual") {
    runValidation();
  }

  if (props.checkInput) {
    emit("blur", event);
  }
}

// ============================================================================
// Watchers
// ============================================================================
watch(
  () => props.modelValue,
  (newValue) => {
    const stringValue = newValue != null ? newValue.toString() : "";
    if (model.value !== stringValue) {
      model.value = stringValue;
    }
  },
  { immediate: true },
);

watch(model, (newValue) => {
  emit("update:modelValue", newValue);

  // Manejar validación según el modo
  if (props.validationMode === "manual") {
    // En modo manual no validar automáticamente
    return;
  }

  if (props.validationMode === "onBlur" && !touched.value) {
    // En modo onBlur solo validar después de tocar el campo
    return;
  }

  // Modo onInput o después de blur
  runValidation();
});

// Limpiar error externo cuando el usuario empieza a escribir
watch(
  () => props.error,
  (newError) => {
    if (newError && model.value) {
      // Si hay un error externo y el usuario tiene contenido,
      // limpiar la validación interna para mostrar solo el error externo
      validationError.value = "";
    }
  },
);
</script>

<style scoped>
@reference "../../assets/main.css";

.p-select-label {
  display: flex !important;
  align-items: center !important;
}

.label-wrapper {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.text-text-primary {
  margin-right: 4px;
}

.left-icon {
  color: var(--color-icon-left);
  font-size: 14px;
}

:deep(.p-inputtext.pl-10),
:deep(.p-inputtext.pl-10\!),
:deep(.p-inputtext.pl-12) {
  padding-left: 2.5rem !important;
}

/* Specificity boost for dependencies */
:deep(.p-iconfield > .p-inputtext.pl-10),
:deep(.p-iconfield > .p-inputtext.pl-10\!) {
  padding-left: 2.5rem !important;
}
</style>
